import { useEffect, type RefObject } from "react";
import {
  formatMessagesForClipboard,
  getSelectedMessageIds,
  selectionIncludesAnyMessageHeader,
  type ClipboardMessage,
} from "@/lib/clipboard-utils";
import type { RenderUser } from "@/lib/render-user";

interface UseClipboardCopyArgs {
  scrollRef: RefObject<HTMLDivElement | null>;
  channelId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messageById: Map<string, any>;
  renderUserMap: Map<string, RenderUser>;
}

/**
 * Custom copy handler — when the user has selected one or more
 * messages (or selected text inside a message header), build a
 * formatted clipboard payload that includes display names + timestamps
 * + content rather than the raw DOM text-content stitched naively.
 *
 * Bails (and lets the browser do its default copy) when the selection
 * is just a snippet inside a single message body — that's still useful
 * to copy verbatim.
 */
export function useClipboardCopy({
  scrollRef,
  channelId,
  messageById,
  renderUserMap,
}: UseClipboardCopyArgs) {
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleCopy = (e: ClipboardEvent) => {
      const selection = window.getSelection();
      if (!selection) return;

      const selectedIds = getSelectedMessageIds(selection, el);
      if (!selectedIds) return;

      const spansMultiple = selectedIds.length >= 2;
      const includesHeader =
        !spansMultiple && selectionIncludesAnyMessageHeader(selection, el);

      if (!spansMultiple && !includesHeader) return;

      const clipboardMessages: ClipboardMessage[] = [];
      for (const id of selectedIds) {
        const msg = messageById.get(id);
        if (!msg) continue;
        const sender = renderUserMap.get(msg.user_id);
        clipboardMessages.push({
          displayName: sender?.display_name || "Unknown",
          timestamp: msg.created_at,
          content: msg.content,
          kind: msg.kind ?? "text",
          payload: (msg.payload ?? null) as Record<string, unknown> | null,
        });
      }

      if (clipboardMessages.length === 0) return;

      const formatted = formatMessagesForClipboard(clipboardMessages);
      e.clipboardData?.setData("text/plain", formatted);
      e.preventDefault();
    };

    el.addEventListener("copy", handleCopy);
    return () => el.removeEventListener("copy", handleCopy);
  }, [messageById, renderUserMap, channelId, scrollRef]);
}
